// The app: the customer's QuoteLink needs nobody signed in; every other screen
// needs an invited Lens user, read from GET /api/me with the ID token. The consent
// page an AI or app sends someone to signs them in on its own card.
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { Me } from "@phoneware/lens-domain";
import { ApiError, type ActAsEnded, type LensApi } from "./api.js";
import type { AuthClient, AuthLoader } from "./auth.js";
import { matchRoute, navigate, useLocation, type Route } from "./router.js";
import { SessionProvider, accessFor, type Session } from "./session.js";
import { PdfProvider, type PdfOpener } from "./pdf-view.js";
import { Shell } from "./shell.js";
import { ToastHost, Head, useTitle } from "./ui.js";
import { errorText, plural } from "./format.js";
import { ActAsEndedDialog } from "./screens/Activity.js";
import { SignIn, SignInCard, SignInFinish, safeNext } from "./screens/SignIn.js";
import { QuoteLinkPage } from "./screens/QuoteLink.js";
import { ConnectPage } from "./screens/Connect.js";
import { Today } from "./screens/Today.js";
import { Quotes } from "./screens/Quotes.js";
import { QuoteBuilder } from "./screens/QuoteBuilder.js";
import { QuoteIntakePage } from "./screens/QuoteIntake.js";
import { Orders, OrderScreen } from "./screens/Orders.js";
import { MacOrder } from "./screens/MacOrder.js";
import { Customers, CustomerScreen } from "./screens/Customers.js";
import { Catalog } from "./screens/Catalog.js";
import { Settings } from "./screens/Settings.js";
import { Approvals } from "./screens/Approvals.js";
import { Dealers } from "./screens/Dealers.js";
import { Requests, RequestDetail } from "./screens/Requests.js";

export interface AppProps {
  api: LensApi;
  loadAuth: AuthLoader;
  /** Leaves Omni for an app's own address after a consent decision. The browser's location unless a test watches it. */
  leave?: (url: string) => void;
  /** Opens a PDF for drawing; pdf.js in a lazy chunk unless a test hands in its own. */
  openPdf?: PdfOpener;
}

/** A stable default, so the consent page's leave effect does not rerun on every render. */
function leaveForApp(url: string): void {
  window.location.assign(url);
}

type Phase =
  | { kind: "booting" }
  | { kind: "signed-out" }
  | { kind: "loading" }
  | { kind: "signed-in"; me: Me }
  | { kind: "refused"; message: string }
  | { kind: "broken"; message: string };

function NotFound(): React.ReactElement {
  useTitle("Not found");
  return (
    <>
      <Head title="Nothing here" sub="This page does not exist in Omni." />
      <section className="card">
        <div className="empty">Use the menu on the left to get back to your quotes, orders and customers.</div>
      </section>
    </>
  );
}

function Screen({ route, query }: { route: Route; query: URLSearchParams }): React.ReactElement {
  switch (route.name) {
    case "today":
      return <Shell nav="today"><Today /></Shell>;
    case "quotes":
      return <Shell nav="quotes"><Quotes tab="quotes" /></Shell>;
    case "quote-templates":
      return <Shell nav="quotes"><Quotes tab="templates" /></Shell>;
    case "quote-new": {
      const customer = Number(query.get("customer"));
      const from = query.get("from");
      return (
        <Shell nav="quotes">
          <QuoteBuilder
            key={`new:${from ?? ""}:${query.get("customer") ?? ""}`}
            id={null}
            customerId={Number.isInteger(customer) && customer > 0 ? customer : null}
            templateId={from || null}
          />
        </Shell>
      );
    }
    case "quote":
      return <Shell nav="quotes"><QuoteBuilder key={route.id} id={route.id} customerId={null} templateId={null} /></Shell>;
    case "quote-intake":
      return <Shell nav="quotes"><QuoteIntakePage key={route.id} id={route.id} /></Shell>;
    case "orders":
      return <Shell nav="orders"><Orders /></Shell>;
    case "mac-new":
      return <Shell nav="orders"><MacOrder /></Shell>;
    case "order":
      return <Shell nav="orders"><OrderScreen key={route.id} id={route.id} /></Shell>;
    case "customers":
      return <Shell nav="customers"><Customers /></Shell>;
    case "customer":
      return <Shell nav="customers"><CustomerScreen key={route.id} id={route.id} /></Shell>;
    case "catalog":
      return <Shell nav="catalog"><Catalog /></Shell>;
    case "requests":
      return <Shell nav="requests"><Requests /></Shell>;
    case "request":
      return <Shell nav="requests"><RequestDetail key={route.id} id={route.id} /></Shell>;
    case "settings":
      return <Shell nav="settings"><Settings tab={route.tab} /></Shell>;
    case "approvals":
      return <Shell nav="approvals"><Approvals /></Shell>;
    case "dealers":
      return <Shell nav="dealers"><Dealers /></Shell>;
    default:
      return <Shell nav="today"><NotFound /></Shell>;
  }
}

function Portal({ api, loadAuth, openPdf, route, query, path }: AppProps & { route: Route; query: URLSearchParams; path: string }): React.ReactElement {
  const [auth, setAuth] = useState<AuthClient | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "booting" });
  const [round, setRound] = useState(0);

  useEffect(() => {
    let live = true;
    loadAuth().then(
      (client) => {
        if (!live) return;
        setAuth(client);
        setEmail(client.email());
      },
      (err: unknown) => {
        if (live) setPhase({ kind: "broken", message: errorText(err) });
      },
    );
    return () => {
      live = false;
    };
  }, [loadAuth]);

  useEffect(() => (auth ? auth.subscribe(() => setEmail(auth.email())) : undefined), [auth]);

  useEffect(() => {
    if (!auth) return;
    if (!email) {
      setPhase({ kind: "signed-out" });
      return;
    }
    let live = true;
    setPhase((p) => (p.kind === "signed-in" ? p : { kind: "loading" }));
    // A tab can reopen holding a session Omni has since ended: that answer clears it, so ask again as the admin.
    api.me().catch((err: unknown) => (isActAsEnded(err) ? api.me() : Promise.reject(err))).then(
      (me) => {
        if (live) setPhase({ kind: "signed-in", me });
      },
      (err: unknown) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 403) setPhase({ kind: "refused", message: err.message });
        else if (err instanceof ApiError && err.status === 401) void auth.signOut();
        else setPhase({ kind: "broken", message: errorText(err) });
      },
    );
    return () => {
      live = false;
    };
  }, [api, auth, email, round]);

  // Who this tab is now, for the act-as controls below, which run after awaits.
  const meNow = useRef<Me | null>(null);
  meNow.current = phase.kind === "signed-in" ? phase.me : null;
  const [ended, setEnded] = useState<ActAsEnded | null>(null);

  // Omni ended the session under this tab (idle, replaced, the admin or the person changed): back to the admin's own
  // view, with the dialog that says so (mock M1 state 60). The api has already cleared the session id.
  useEffect(
    () =>
      api.onActAsEnded((e) => {
        setEnded(e);
        api.me().then(
          (me) => {
            setPhase({ kind: "signed-in", me });
            navigate("/");
          },
          () => setRound((r) => r + 1),
        );
      }),
    [api],
  );

  const needsUser = route.name !== "signin" && route.name !== "signin-finish";
  const nextParam = query.get("next");
  useEffect(() => {
    if (phase.kind === "signed-out" && needsUser) {
      const here = `${path}${window.location.search}`;
      navigate(here === "/" ? "/signin" : `/signin?next=${encodeURIComponent(here)}`, { replace: true });
    }
    if (phase.kind === "signed-in" && route.name === "signin") navigate(safeNext(nextParam), { replace: true });
  }, [phase.kind, needsUser, route.name, path, nextParam]);

  // Signing out while acting ends the session first, so the audit log says why it ended. If Omni can't be reached, the
  // session still ends on its own after its idle minutes.
  const signOut = useCallback(() => {
    void (async () => {
      const id = api.actAs.get();
      if (id) {
        await api.stopActAs(id, "signed_out").catch(() => undefined);
        api.actAs.set(null);
      }
      if (auth) await auth.signOut();
    })();
  }, [api, auth]);
  const refreshMe = useCallback(() => setRound((r) => r + 1), []);

  const startActingAs = useCallback(
    async (email: string) => {
      const view = await api.startActAs(email);
      api.actAs.set(view.sessionId);
      const me = await api.me();
      setPhase({ kind: "signed-in", me });
      navigate("/");
    },
    [api],
  );

  const stopActing = useCallback(async (landing?: string): Promise<string> => {
    const id = api.actAs.get();
    const acting = meNow.current?.actingAs ?? null;
    if (!id || !acting) return "";
    await api.stopActAs(id, "stopped");
    api.actAs.set(null);
    // As the admin again: how many changes the session recorded, from the same log the dealer reads.
    const changes = await api.activity().then(
      (view) => view.sessions.find((s) => s.id === id)?.changes.length ?? null,
      () => null,
    );
    const me = await api.me();
    setPhase({ kind: "signed-in", me });
    navigate(landing ?? `/dealers?d=${encodeURIComponent(acting.target.dealerId)}`);
    return `Stopped acting as ${acting.target.name}.${changes === null ? "" : ` ${plural(changes, "change")} recorded in Phoneware activity.`}`;
  }, [api]);

  if (route.name === "signin-finish") {
    if (auth) return <SignInFinish auth={auth} me={phase.kind === "signed-in" ? phase.me : null} refused={phase.kind === "refused"} onSignedIn={() => setEmail(auth.email())} />;
    return phase.kind === "broken" ? <Problem message={phase.message} /> : <Waiting />;
  }
  if (phase.kind === "refused") {
    return (
      <SignInCard>
        <h1>Not invited yet</h1>
        <p className="signin-lede">{phase.message}</p>
        <button className="btn primary wide" type="button" onClick={signOut}>
          Use a different email
        </button>
      </SignInCard>
    );
  }
  if (phase.kind === "broken") return <Problem message={phase.message} />;
  if (route.name === "signin") return phase.kind === "signed-out" || phase.kind === "booting" ? <SignIn api={api} auth={auth} next={nextParam} /> : <Waiting />;
  if (phase.kind !== "signed-in" || !auth) return <Waiting />;

  const session: Session = { me: phase.me, api, auth, signOut, refreshMe, startActingAs, stopActing, ...accessFor(phase.me) };
  const screen = (
    <ToastHost>
      <Screen route={route} query={query} />
      {ended ? <ActAsEndedDialog ended={ended} onClose={() => setEnded(null)} /> : null}
    </ToastHost>
  );
  return <SessionProvider value={session}>{openPdf ? <PdfProvider value={openPdf}>{screen}</PdfProvider> : screen}</SessionProvider>;
}

/** Omni's 409 for a session it no longer honors. */
function isActAsEnded(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && typeof err.body === "object" && err.body !== null && "error" in err.body && err.body.error === "act_as_ended";
}

function Waiting(): React.ReactElement {
  return (
    <SignInCard>
      <h1>Opening Omni</h1>
      <p className="signin-lede">One moment.</p>
    </SignInCard>
  );
}

function Problem({ message }: { message: string }): React.ReactElement {
  return (
    <SignInCard>
      <h1>Omni did not load</h1>
      <p className="signin-lede">{message}</p>
      <button className="btn primary wide" type="button" onClick={() => window.location.reload()}>
        Try again
      </button>
    </SignInCard>
  );
}

export function App({ api, loadAuth, leave = leaveForApp, openPdf }: AppProps): React.ReactElement {
  const { path, query } = useLocation();
  const route = matchRoute(path);
  if (route.name === "quotelink") return <QuoteLinkPage key={route.token} api={api} token={route.token} />;
  if (route.name === "connect") return <ConnectPage key={route.requestId} api={api} loadAuth={loadAuth} requestId={route.requestId} leave={leave} />;
  return <Portal api={api} loadAuth={loadAuth} openPdf={openPdf} route={route} query={query} path={path} />;
}
